import React, { useState } from 'react';
import { Row, Col } from 'react-bootstrap';
import { OrderProduct } from './OrderTypes';

interface ProductSelectionStepProps {
  productsData: OrderProduct[];
  selectedProductId: string;
  onProductChange: (id: string) => void;
  onNext: () => void;
  loading: boolean;
}

const ProductSelectionStep: React.FC<ProductSelectionStepProps> = ({ productsData, selectedProductId, onProductChange, onNext, loading }) => {
  const [hoveredId, setHoveredId] = useState<string | null>(null);

  return (
    <div className="step-content-fade">
      <h4 className="fw-bold text-dark mb-2" style={{ fontSize: 'clamp(18px, 5vw, 24px)', letterSpacing: '-0.5px' }}>
        Choose Print Category
      </h4>
      <p className="text-muted mb-5" style={{ fontSize: '14px' }}>Select the type of printing service you need</p>
      {loading ? (
        <div className="text-center py-5 text-muted">
          <i className="fas fa-spinner fa-spin me-2" style={{ fontSize: '24px' }}></i>
          <p className="mt-3">Loading products...</p>
        </div>
      ) : (
        <Row className="g-4">
          {productsData.map(prod => {
            const isSelected = selectedProductId === prod.id;
            const isHovered = hoveredId === prod.id;
            return (
              <Col key={prod.id} xs={12} sm={6}>
                <div
                  onClick={() => onProductChange(prod.id)}
                  onMouseEnter={() => setHoveredId(prod.id)}
                  onMouseLeave={() => setHoveredId(null)}
                  style={{
                    height: '100%',
                    cursor: 'pointer',
                    backgroundColor: '#fff',
                    borderRadius: '14px',
                    border: isSelected ? '2px solid #e91e8c' : '1px solid #eaeef5',
                    overflow: 'hidden',
                    transition: 'all 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                    transform: isHovered ? 'translateY(-8px) scale(1.02)' : 'translateY(0) scale(1)',
                    boxShadow: isSelected
                      ? '0 12px 30px rgba(233, 30, 140, 0.2)'
                      : isHovered
                      ? '0 12px 30px rgba(233, 30, 140, 0.15)'
                      : '0 4px 15px rgba(0, 0, 0, 0.08)',
                    display: 'flex',
                    flexDirection: 'column',
                  }}
                >
                  {/* Image Section */}
                  <div
                    style={{
                      height: '160px',
                      overflow: 'hidden',
                      position: 'relative',
                      background: 'linear-gradient(135deg, #f5f7fa 0%, #eaeef5 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <img
                      src={prod.img}
                      alt={prod.name}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                        transition: 'transform 0.4s cubic-bezier(0.34, 1.56, 0.64, 1)',
                        transform: isHovered ? 'scale(1.12)' : 'scale(1)',
                      }}
                    />

                    {/* Gradient Overlay */}
                    <div
                      style={{
                        position: 'absolute',
                        top: 0,
                        left: 0,
                        right: 0,
                        bottom: 0,
                        background: isHovered
                          ? 'linear-gradient(135deg, rgba(233, 30, 140, 0.2) 0%, rgba(59, 91, 219, 0.2) 100%)'
                          : 'linear-gradient(135deg, rgba(233, 30, 140, 0.05) 0%, rgba(59, 91, 219, 0.05) 100%)',
                        transition: 'all 0.3s ease',
                      }}
                    />

                    {/* Category Icon Badge */}
                    {prod.icon && (
                      <div
                        style={{
                          position: 'absolute',
                          top: '12px',
                          right: '12px',
                          width: '45px',
                          height: '45px',
                          background: 'rgba(255, 255, 255, 0.95)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 6px 16px rgba(0, 0, 0, 0.12)',
                          opacity: isHovered ? 1 : 0.8,
                          transform: isHovered ? 'scale(1.1)' : 'scale(1)',
                          transition: 'all 0.3s ease',
                        }}
                      >
                        <i
                          className={prod.icon}
                          style={{
                            fontSize: '18px',
                            background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                            WebkitBackgroundClip: 'text',
                            WebkitTextFillColor: 'transparent',
                          }}
                        ></i>
                      </div>
                    )}

                    {/* Selection Indicator */}
                    {isSelected && (
                      <div
                        style={{
                          position: 'absolute',
                          bottom: '12px',
                          right: '12px',
                          width: '32px',
                          height: '32px',
                          background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                          borderRadius: '50%',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          boxShadow: '0 6px 16px rgba(233, 30, 140, 0.3)',
                          animation: 'pulse 2s infinite',
                        }}
                      >
                        <i className="fas fa-check" style={{ color: '#fff', fontSize: '14px', fontWeight: '700' }}></i>
                      </div>
                    )}
                  </div>

                  {/* Content Section */}
                  <div style={{ padding: '20px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                    <h5
                      style={{
                        fontSize: 'clamp(14px, 3vw, 16px)',
                        fontWeight: '700',
                        color: '#1a1a1a',
                        margin: '0 0 8px 0',
                        letterSpacing: '-0.3px',
                      }}
                    >
                      {prod.name}
                    </h5>
                    <p
                      style={{
                        fontSize: '13px',
                        color: '#666',
                        margin: '0 0 16px 0',
                        lineHeight: '1.6',
                        flex: 1,
                        display: '-webkit-box',
                        WebkitLineClamp: 2,
                        WebkitBoxOrient: 'vertical',
                        overflow: 'hidden',
                      }}
                    >
                      {prod.desc}
                    </p>

                    {isSelected && (
                      <button
                        type="button"
                        style={{
                          padding: '10px 16px',
                          borderRadius: '8px',
                          fontSize: 'clamp(12px, 2.5vw, 14px)',
                          fontWeight: '700',
                          border: 'none',
                          cursor: 'pointer',
                          background: 'linear-gradient(135deg, #e91e8c 0%, #3b5bdb 100%)',
                          color: '#fff',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          gap: '8px',
                          transition: 'all 0.3s ease',
                          boxShadow: '0 6px 16px rgba(233, 30, 140, 0.3)',
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = 'scale(1.05)';
                          e.currentTarget.style.boxShadow = '0 8px 24px rgba(233, 30, 140, 0.4)';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 6px 16px rgba(233, 30, 140, 0.3)';
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onNext();
                        }}
                      >
                        <span>Next: Configure Options</span>
                        <i className="fas fa-arrow-right" style={{ fontSize: 'clamp(10px, 2vw, 12px)' }}></i>
                      </button>
                    )}
                  </div>
                </div>
              </Col>
            );
          })}
        </Row>
      )}

      <style>{`
        @keyframes pulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.1); }
          100% { transform: scale(1); }
        }
      `}</style>
    </div>
  );
};

export default ProductSelectionStep;
